import React from 'react';
import { useNavigate } from 'react-router-dom';
import { DocumentTextIcon, GlobeAltIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import type { ChatSource } from '@archie/api-shared';

interface SourceCardProps {
  source: ChatSource;
  index: number;
  highlighted: boolean;
  sourceKey: string;
}

const titleFor = (source: ChatSource): string => {
  if (source.kind === 'web') {
    if (source.title && source.title.length > 0) return source.title;
    try {
      return new URL(source.url).hostname;
    } catch {
      return source.url;
    }
  }
  if (source.name && source.name.length > 0) return source.name;
  return source.sourceId
    ? `Source ${source.sourceId.slice(0, 8)}`
    : source.documentId
      ? `Doc ${source.documentId.slice(0, 8)}`
      : 'Knowledge base';
};

const subtitleFor = (source: ChatSource): string | null => {
  if (source.kind === 'web') {
    try {
      return new URL(source.url).hostname;
    } catch {
      return null;
    }
  }
  if (typeof source.score === 'number') {
    return `Relevance ${(source.score * 100).toFixed(0)}%`;
  }
  return null;
};

const SourceCard: React.FC<SourceCardProps> = ({ source, index, highlighted, sourceKey }) => {
  const navigate = useNavigate();
  const title = titleFor(source);
  const subtitle = subtitleFor(source);
  const Icon = source.kind === 'web' ? GlobeAltIcon : DocumentTextIcon;

  const onClick = () => {
    if (source.kind === 'web') {
      window.open(source.url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (source.kbId) {
      const params = source.sourceId ? `?highlight=${encodeURIComponent(source.sourceId)}` : '';
      navigate(`/organization/knowledge-bases/${source.kbId}/sources${params}`);
    }
  };

  const interactive = source.kind === 'web' || !!source.kbId;

  return (
    <button
      type="button"
      data-source-key={sourceKey}
      onClick={interactive ? onClick : undefined}
      disabled={!interactive}
      className={`group w-full text-left rounded-lg border p-3 transition-all ${
        highlighted
          ? 'border-(--color-accent) ring-2 ring-(--color-accent) bg-(--color-surface-default)'
          : 'border-(--color-border-default) bg-(--color-surface-subtle) hover:bg-(--color-surface-default)'
      } ${interactive ? 'cursor-pointer' : 'cursor-default'}`}
    >
      <div className="flex items-start gap-2">
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-(--color-border-default) text-[11px] font-semibold text-(--color-text-muted) shrink-0 mt-0.5">
          {index + 1}
        </span>
        <Icon className="h-4 w-4 text-(--color-text-muted) shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <span className="text-sm font-medium text-(--color-text-primary) line-clamp-2 break-words">{title}</span>
            {interactive && (
              <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 text-(--color-text-muted) opacity-0 group-hover:opacity-100 transition-opacity shrink-0 mt-0.5" />
            )}
          </div>
          {subtitle && <div className="text-xs text-(--color-text-muted) mt-0.5 truncate">{subtitle}</div>}
          {source.snippet && (
            <p className="text-xs text-(--color-text-secondary) mt-1.5 line-clamp-3 break-words whitespace-pre-wrap">
              {source.snippet}
            </p>
          )}
        </div>
      </div>
    </button>
  );
};

export default SourceCard;
